/**
 * pages/overview.ts —— 运营总览（W2-8，P1 最后一个模块）
 * 来历：一期 [SEC:page-overview] 的 overviewHtml()，版式沿用，**但删了四块**。
 *
 * ## 🔴 2026-08-20 管理员在原型截图上红框标注的四块，**已删，不要"补回来"**
 *   ① Hero 下方「需跟进 N 支」整条（含队伍 chip 与「点击定位到矩阵对应行」）
 *   ② 矩阵工具栏整行（左侧着色切换器 + 右侧四个筛选 chip）
 *   ③ 矩阵「风险 KR」列
 *   ④ 矩阵「14天活动」+「参与人次」两列
 * 连带结果：**运营总览不再有催办入口**（管理员 2026-08-20 确认不需要），
 * 催办只剩 Hero 副行「上一期还有 N 支未补交」与双周报页历史期。
 * ⚠ 口径函数一个都没删——`riskOf` / `act14` / `pax14` 在 Hero、队伍详情、OKR 页仍是消费者。
 * 论证见 `03_归档/13_二期需求变更影响评估.md` §二之一。
 *
 * ## ➕ 同日新增的两个入口（在页面上，不占接口）
 *   **打卡入口** → 直接打开日常打卡的新增弹窗（弹窗本体只有一份，见 `pages/checkins.ts`）
 *     ⚠ 按钮上的字 2026-08-23 由「一键打卡」缩成「打卡」：`+` 号已经表达了「新增」，
 *     「一键」不承载信息、只把按钮撑宽 27px。**两处入口（Hero / 最近活动空态）必须同名**。
 *   **AI 助手** → **右侧抽屉**（`shared/assistant.ts`，2026-08-24）
 * ⚠ 两个入口**只在首页**，AI 助手**不进侧栏 NAV**，也不在别的页面开第二个入口。
 *
 * ## 三条口径（写反了不会报错，只会悄悄对不上账）
 * 1. **一个数都不在这里算**（红线 4）：平均进度、提交率、逾期支数、落后期数、「几天前」
 *    全部服务端算好下发；**排序也在服务端**，前端只按 `sort` / `dir` 高亮表头。
 * 2. **矩阵展示「最新提交到第几期」，不展示本期交没交、不展示逾期**——
 *    矩阵回答「谁进度落后」，Hero 回答「本期谁没交、上期谁欠着」，两者不是替代关系。
 * 3. **无进行中周期是正常业务态**（红线 3.5）：提交率降级为上一期结算值并**标注期号**。
 */
import type { Activity, OverviewMatrixRow, OverviewSortKey, OverviewView } from '@shared-types';
import { overview as overviewApi, persons as personApi } from '../api/index.js';
import { emptyHtml } from '../shared/common.js';
import { $, esc } from '../shared/dom.js';
import { EIC, IC } from '../shared/icons.js';
import { state } from '../shared/state.js';
import { go } from '../router/index.js';
import { refreshCurrent } from '../router/render.js';
import { countUp, mountHeroFx } from '../shared/herofx.js';
import { definePage } from './types.js';
import { cardHtml, openNewCheckin, viewModal } from './checkins.js';

export interface OverviewData {
  view: OverviewView;
}

/* ===================== 渲染（纯函数） ===================== */

/** 进度条四档：同色系明度递进，低 <60 / 中 60—79 / 高 80—99 / 完成 100（一期 q1—q4 同款） */
const qOf = (p: number): string => (p >= 100 ? 'q4' : p >= 80 ? 'q3' : p >= 60 ? 'q2' : 'q1');

/**
 * 「最新提交」徽标的档位。类名沿用 q1—q4，**但视觉自 2026-08-24 起只有两档**
 * （实现在 `22_overview_hero.css`，本文件只负责挂档位）：
 *   q4 / q3 = 最新提交是本期或上一期 → 同一枚空心金，**两者不再用颜色区分**
 *   q2 / q1 / none = 更早或从未提交   → 中性灰，**不再标红**
 * 🔴 口径一个字没改，仍然是「最新提交到第几期」；改的只是配色。
 * ⚠ 类名保持四档**不要合并**：TS 这边不承担配色，日后若要再分档，改 CSS 一处即可。
 * ⚠ 界面上**不另加「落后 N 期」文字**（`08` §六 D6）；也**不做 12 期 12 个色相**。
 */
const lagTier = (lag: number | null): string =>
  lag === null ? 'none' : lag <= 0 ? 'q4' : lag === 1 ? 'q3' : lag === 2 ? 'q2' : 'q1';

/**
 * Hero 四格的环形指标（2026-08-23 方向 D，借自方案 C）。
 *
 * 🔴 **不产生任何新口径**（红线 2 / 4）：环画的是**同一格里那两个已由服务端下发的数字**的比值，
 *   前端只做一次除法用于画弧，**不把由弧反推的数字显示出来**，也不落库。
 *   「44.5%」这种孤立百分数读者得自己脑补满分是多少，画成环之后分母是看得见的——
 *   剩下那段空槽本身就是信息，这是这枚环全部的价值。
 * ⚠ 分母为 0 画空环，**不除零**（红线 3.5 同源：无进行中周期时 expected 可能为 0）。
 * ⚠ `RING_C` 必须与 `22_overview_hero.css` 的 `stroke-dasharray` 一致，改一处要改两处。
 */
const RING_C = 131.9; // 2π × 21（r=21，与 CSS 里的 <circle r> 一致）

/**
 * 环的四种色调（样式在 `22_overview_hero.css` ②）：
 *   ''   亮金 = 达成 / 已提交（双周报）　'soft' 浅金 = 覆盖（活跃队伍）
 *   'warn' 砖红 = 需要动作（上一期逾期未交）　'mute' 白 = 中性占比（诉求，**不是达成，不许用金**）
 */
type RingTone = '' | 'soft' | 'warn' | 'mute';

function ringHtml(num: number, den: number, tone: RingTone = ''): string {
  const p = den > 0 ? Math.max(0, Math.min(1, num / den)) : 0;
  return `<span class="hstat-ring${tone ? ' ' + tone : ''}" aria-hidden="true"><svg width="48" height="48" viewBox="0 0 48 48">
        <circle class="bg" cx="24" cy="24" r="21"/>
        <circle class="fg" cx="24" cy="24" r="21" style="stroke-dashoffset:${(RING_C * (1 - p)).toFixed(1)}"/>
      </svg></span>`;
}

/** Hero 左侧的周期块：四种状态四套文案，**不能写成一句通用的** */
export function cycleHtml(v: OverviewView): string {
  const { current, lastClosed, next } = v.hero.period;
  if (current) {
    /* ⚠ 2026-08-23 去掉「提交截止 YYYY-MM-DD 23:59:59」那一段：
       截止 = 周期结束当日 24:00（红线 3），**结束日期本身已经把截止说清楚了**，
       再写一遍绝对时间只是把这行撑成两行。真正要看的倒计时在 Hero 块③「距截止还有 N 天」。
       ⚠ `current.due` 字段仍在下发、口径未动，只是这里不展示。 */
    return `<b>${esc(current.name)}</b>
      <span class="num">${esc(current.start)} ~ ${esc(current.end)}</span>`;
  }
  // 🔴 空当期是正常业务态（周期临近再建 + 跳过国庆年末），文案里不出现「错误 / 异常」
  /* 🔴 2026-09-09：**已建好但还没到开始日**的期要把期号和起止日期报出来，只标「待开始」。
     原先这里直接掉到「尚未创建任何周期」，而库里第 1 期是建好的——那句话是**假的**。
     ⚠ 它只是文案：当期仍然是 null，提交率 / 逾期 / 活动窗口一个都不看这一期（红线 3.5）。
     ⚠ **不写「N 天后开始」倒计时**（2026-09-09 用户定，当天发现当天撤）：开始日期就写在旁边，
       再补一句倒计时是同一件事说两遍，还把这一行挤成两行。 */
  if (next) {
    return `<b>${esc(next.name)}</b><em class="soon">待开始</em>
      <span class="num">${esc(next.start)} ~ ${esc(next.end)}</span>`;
  }
  if (lastClosed) {
    return `<b>${esc(lastClosed.name)}已结束</b>
      <span>第 ${lastClosed.no + 1} 期尚未创建</span>`;
  }
  return `<b>尚未创建任何周期</b><span>由项目组管理员在管理后台创建第一期</span>`;
}

/** 一格的骨架：环 + 标签 / 大数 + 单位 / 两行说明。⚠ 大数挂 `mo-num`，首次进页由 herofx 跳数 */
function statHtml(ring: string, lab: string, num: string, unit: string, fr: string, sm = true): string {
  return `
        <div class="hstat">
          <div class="hstat-top">${ring}<div class="hstat-lab">${lab}</div></div>
          <div class="hstat-val"><span class="n num${sm ? ' sm' : ''} mo-num">${num}</span>${
            unit ? `<span class="u">${unit}</span>` : ''
          }</div>
          <div class="hstat-fr">${fr}</div>
        </div>`;
}

/**
 * 🔴 双周报一格（2026-10-07 由两格合并，用户拍板）。
 * 各队都会交，只是集中在最后一个周末或新一期头几天——原来两格大部分时间一个是 100%、一个是 0。
 * 现在**一格按阶段讲一个期**（阶段由服务端 `report.phase` 判，这里不比较任何日期，红线 4）：
 *   prev 新一期头 7 天 / 空当期 → 「上期双周报提交」提交率（哪怕 100% 也照样讲上一期）
 *   cur  第 8 天起 / 首期       → 「本期双周报」已交 / 应交 + 距截止（**不给百分比**），第二行讲上期交齐没有
 * 🔴 上一期结束头 3 天还有队没交：普通灰字「还有 X 支未提交」，不用砖红、不写逾期；
 *   第 4 天起（`prevWarn`）环变砖红、写「逾期 N 天」。宽限 1 天是非正式习惯，**界面不写原因**。
 */
export function reportHtml(v: OverviewView): string {
  const b = v.hero.report;
  const n = (x: number) => `<b class="num">${x}</b>`;
  if (b.phase === 'prev') {
    const p = b.prev!;
    const tail =
      p.owed === 0
        ? '已全部交齐'
        : b.prevWarn
          ? `还有 ${n(p.owed)} 支逾期 ${n(p.owedDays)} 天`
          : `还有 ${p.owed} 支未提交`; // ⚠ 弱提示：数字不加粗
    return statHtml(
      ringHtml(p.rate, 100, b.prevWarn ? 'warn' : ''),
      '上期双周报提交',
      p.rate.toFixed(1),
      '%',
      `已提交 ${n(p.submitted)} / ${n(p.expected)} 支<br>${tail}`,
      false,
    );
  }
  if (b.phase === 'cur') {
    const c = b.cur!;
    const d = b.daysToDue ?? 0;
    /* ⚠ 第二行讲**上期**提交情况，不写本期「还差 N 支」（用户 2026-10-07：大数已经说明本期交了几支） */
    const p = b.prev;
    const tail = !p ? '首期进行中' : p.owed === 0 ? '上期周报已交齐' : `上期已交 ${p.submitted} / ${p.expected}`;
    return statHtml(
      ringHtml(c.submitted, c.expected),
      '本期双周报',
      String(c.submitted),
      `/ ${c.expected} 支已提交`,
      `${d === 0 ? '今天截止' : `距截止还有 ${n(d)} 天`}<br>${tail}`,
    );
  }
  const next = v.hero.period.next;
  return statHtml(ringHtml(0, 1), '双周报', '—', '', next
    ? `${esc(next.name)}尚未开始<br>开始后在此显示`
    : '尚未创建任何周期<br>新周期建好后在此显示');
}

/**
 * 近 14 天活跃队伍（2026-10-07 由「最近 30 天活动」改来）。
 * 30 天窗太宽，「覆盖 12 / 12」恒为满，看不出谁已经停下来；14 天 = 一个双周期，全站只有这一个滚动窗口。
 * ⚠ 环画的是**覆盖率 teamsCovered / teams**，大数也是它；活动条数与人次降到说明行，**同一个 14 天窗**。
 */
export function activeHtml(v: OverviewView): string {
  const h = v.hero;
  /* ⚠ 2026-10-07 第三轮：说明行只写条数与人次两行，**不写「N 支近 14 天没有活动」**（用户要求，大数与分母已经说明了） */
  return statHtml(
    ringHtml(h.teamsCovered, h.teams, 'soft'),
    '近 14 天活跃队伍',
    String(h.teamsCovered),
    `/ ${h.teams} 支`,
    `活动 <b class="num">${h.act14}</b> 条<br>参与 <b class="num">${h.pax14}</b> 人次`,
  );
}

/**
 * 双周报诉求（2026-10-07 新增）：上一期已提交的双周报里，写了实际「资源诉求」的队伍数。
 * 🔴 **只写资源诉求；没有资源诉求时才写风险**（用户 2026-10-07）——两件事不并排出现。
 * 判据是服务端的 `hasNeed` / `hasRisk`（「本期无。」之类不算），这里只照着画。
 * ⚠ **只给数字不给队名**：2026-08-20 管理员撤掉了首页的催办名单，具体是谁去双周报页看。
 * ⚠ 环用中性白（`mute`）：诉求占比不是「达成」，画成金色就是颜色语义污染。
 */
export function asksHtml(v: OverviewView): string {
  const a = v.hero.asks;
  if (!a) return statHtml(ringHtml(0, 1, 'mute'), '双周报诉求', '—', '', '尚未创建任何周期<br>首份双周报提交后统计');
  const lab = `${a.scope === 'prev' ? '上期' : '本期'}双周报诉求`;
  /* ⚠ 2026-10-07 第三轮：说明行**不再写「第 N 期已提交 X 支」**——双周报那一格已经写了，两格重复（用户指出）。
     第四轮：第一行写风险情况、第二行「详见双周报页」。风险与诉求是同一期（上期）的，所以**不写「本期」二字**。 */
  if (!a.submitted) return statHtml(ringHtml(0, 1, 'mute'), lab, '—', '', '暂无已提交的双周报');
  const riskLine = a.risk > 0 ? `${a.risk} 支有风险预警` : '暂无风险预警';
  if (a.need === 0 && a.risk > 0) {
    // 没有资源诉求时大数改讲风险，第一行就说没有资源诉求，免得风险说两遍
    return statHtml(ringHtml(a.risk, a.submitted, 'mute'), lab, String(a.risk), '支有风险预警', '暂无资源诉求<br>详见双周报页');
  }
  return statHtml(ringHtml(a.need, a.submitted, 'mute'), lab, String(a.need), '支有资源诉求', `${riskLine}<br>详见双周报页`);
}

/**
 * Hero。
 * ⚠ 第一格（团队平均 OKR 进度）的说明行 **2026-08-24 起不写队数**（原「N 支队伍算术平均」）：
 *   那个 N 是 `okrTeams`（**已建 KR 的队**——0 条 KR 的队按 `avgOkrAll()` 的口径不进分母），
 *   而它正下方的矩阵写着「共 5 支队伍」，两个数并排出现看着像算错了（用户 2026-08-24 提出）。
 * 🔴 **口径一个字都没动**：分母仍然只数已建 KR 的队，改的只是这一行不再把它显示出来。
 * ⚠ `okrTeams` 仍在出参里、OKR 页页头还在用它，**不要顺手把字段删掉**。
 * ⚠ 说明写在这里、不写进模板串里的 HTML 注释——那里的反引号会把模板字符串提前闭合。
 */
export function heroHtml(v: OverviewView): string {
  const h = v.hero;

  return `
  <section class="hero" aria-label="训练营全局态势">
    <!-- 环境层（2026-10-07 起改为一张画布）：光晕 + 点阵 + 星链全由 shared/herofx.ts 画。
         画布不可用时 .hero 不挂 .fx-on，CSS 的 ::before 点阵照常兜底。
         ⚠ 纯装饰、不承载任何信息，所以是空元素 + aria-hidden -->
    <canvas class="hero-fx" aria-hidden="true"></canvas>
    <div class="hero-body">
      <div class="hero-l">
        <div>
          <h1 class="hero-h1">运营总览</h1>
          <p class="hero-sub"><span class="num">${h.teams}</span> 支队伍 ·
            <span class="num">${h.members}</span> 名正式成员${
              h.volunteers ? ` · <span class="num">${h.volunteers}</span> 名志愿者` : ''
            }</p>
        </div>
        <div class="hero-cyc">${cycleHtml(v)}</div>
        ${actHtml()}
      </div>

      <div class="hero-r">
        <div class="hstat">
          <div class="hstat-top">${ringHtml(h.avgOkr, 100)}<div class="hstat-lab">团队平均 OKR 进度</div></div>
          <div class="hstat-val"><span class="n num mo-num">${h.avgOkr.toFixed(1)}</span><span class="u">%</span></div>
          <div class="hstat-fr">全部队伍算术平均<br>所有 KR 同权重</div>
        </div>
${reportHtml(v)}${asksHtml(v)}${activeHtml(v)}
      </div>
    </div>
  </section>`;
}

/**
 * 两个页面级入口（2026-08-20 新增；**2026-08-23 从独立的白色横带迁进 Hero 左栏**）。
 *
 * 🔴 迁移的理由不是"位置不好看"：原来那条 `.ov-quick` 自己占一整条白带（两个按钮 + 一句提示语，
 *   撑起 55px），把 Hero 和矩阵推开，页面被切成四条平行的白带。并进 Hero 之后整条带子消失。
 * ⚠ 这里【不是】被删掉的「需跟进条」，别把催办 chip 加回来；那条与 `.ov-quick` 现在都不存在。
 * ⚠ AI 助手按钮上**不写任何状态词**：它是给人看见的能力亮点。样式见 `22_overview_hero.css`。
 * 🔴 **2026-08-24：它不再是路由跳转，而是打开右侧抽屉**（`shared/assistant.ts`）。
 *   `#/assistant` 那条路由与那一页空态**已整条退役**——别把 `data-nav="#/assistant"` 改回来。
 *   入口**只有这一枚**（管理员明确「其他地方暂不需要 AI 快速入口」）。
 */
function actHtml(): string {
  return `
        <div class="hero-act">
          <button class="hbtn" data-ov="checkin">${IC.plus}打卡</button>
          <button class="hbtn ai" data-ai="open">${IC.ai}<b>AI 助手</b></button>
        </div>`;
}

function rowHtml(r: OverviewMatrixRow, v: OverviewView): string {
  /* 🔴 首期进行中 = **一期都还没结束过**（`period.lastClosed === null`）。
     此时"没交"是营期刚开始的常态，不是欠账：按红线 3，周期进行中**不存在逾期**，
     而 brick 是「需要动作」色——整列标红会和 Hero 块③「距截止还有 N 天」自相矛盾，
     等于在第一周把全营点名一遍。所以首期走中性档 `.pend`，
     **等到有期结束了还没交，才用 brick**（2026-08-24 用户确认）。
     ⚠ 2026-09-10：这一档的文案定为「尚未提交」（原「本期待提交」），
       且**不再有任何填充色块**（灰字裸文，样式见 `22_overview_hero.css` ⑤之二）——
       首期全营十二行都会落在这一档，任何底色铺满一整列都会变成噪点。 */
  const firstPeriod = v.hero.period.lastClosed === null;
  const subNo =
    r.latestSubmittedPeriodNo === null
      ? /* ⚠ 从未提交：点进去落到**基准期**的双周报（那是唯一能填的一期） */
        `<button class="sub-no ${firstPeriod ? 'pend' : 'none'}" data-nav="#/reports${
          v.hero.baseNo === null ? '' : '/p' + v.hero.baseNo
        }/${esc(r.id)}" title="${
          firstPeriod
            ? '营期首期进行中，本期尚未提交（未到截止，不算逾期），点击进入双周报'
            : '该队从未提交过任何一期双周报，点击进入双周报'
        }">${firstPeriod ? '尚未提交' : '未提交'}</button>`
      : `<button class="sub-no ${lagTier(r.lag)}" data-nav="#/reports/p${r.latestSubmittedPeriodNo}/${esc(r.id)}"
          title="${r.lag === 0 ? '已提交到当前期' : `最新提交为第 ${r.latestSubmittedPeriodNo} 期，落后 ${r.lag} 期`}，点击查看该期双周报">第 ${r.latestSubmittedPeriodNo} 期</button>`;

  /* `.sub-lb` 只在手机端显示：手机上表头整行隐藏，徽标「第 N 期」单独出现意义不明
     （2026-09-29 用户反馈）；桌面端有表头「最新提交」，它 display:none。 */
  return `
  <tr data-team="${esc(r.id)}" tabindex="0" aria-label="查看 ${esc(r.name)} 详情">
    <td class="c-team"><b>${esc(r.name)}</b><s class="num">${esc(r.code)}</s></td>
    <td class="c-topic"><b title="${esc(r.topic)}">${esc(r.topic)}</b>
      <s>队长 ${esc(r.leadName ?? '未指定')} · <span class="num">${r.memberCount}</span> 人</s></td>
    <td>
      ${
        /* 一条 KR 都没有 → **画空进度条，但右边百分比位留空**（2026-09-09 用户第二次定）。
           `okr` 在「还没建 KR」与「建了但全是 0」两种情况下都是 0（`okrOf()` 无 KR 返回 0），
           只有 `krCount` 能分开它们，所以这一格的形态由 `krCount` 决定，**不是 `okr === 0`**。
           ⚠ 这一格改过三轮，别再来回搬：
             ① 最早是空条 +「—」；② 2026-09-09 上午整格留空（整列十二行空白太散）；
             ③ 2026-09-09 下午空条 + 不写字；④ 2026-09-10（现行）**空条 +「未建 OKR」**——
             不写字那版在营期未启动时整列都是空的，看着像数据没加载出来；写「0%」又会把
             「还没开始」说成「进度为零」，所以直接把状态说出来。
           ⚠ 这个 span **不要删**，删了进度条会顶到右边去；它**不带 `num`**（不是数字，
             等宽数字字体会把「未建 OKR」拉得很怪），定宽由 44px 放宽到 52px 且
             **两种形态共用**（否则轨道右端差 8px），样式见 `22_overview_hero.css` ⑤之三。 */
        r.krCount === 0
          ? `<span class="okrwrap"><span class="okrbar"><i style="width:0%"></i></span>
        <span class="okrpct nokr">未建 OKR</span></span>`
          : `<span class="okrwrap"><span class="okrbar ${qOf(r.okr)}"><i style="width:${r.okr}%"></i></span>
        <span class="okrpct num">${r.okr}<em>%</em></span></span>`
      }
    </td>
    <td class="c-sub"><span class="sub-lb">最新双周报</span>${subNo}</td>
    <td class="c-brief">${
      r.brief
        ? /* 🔴 2026-08-24 期号前缀整个撤掉（原先落后的队会带一句「（第 N 期）」）：
             左边「最新提交」那一列已经写着期号，同一行里说两遍是重复；
             撤掉之后这一列只剩摘要正文，`.bno` 样式一并删除，**不要再加回来**。 */
          `<span title="${esc(r.brief.text)}">${esc(r.brief.text)}</span>`
        : /* ⚠ 首期进行中说「首份双周报提交后展示」——那时全营都没交过，"暂无"听着像出了状况，
             其实是还没到时候。已经有期结束过之后仍然没有摘要，才是真的"暂无"。
             🔴 2026-09-10 由「待填写首期摘要」改成现在这句：矩阵是给全员看的**说明**，
               不是给某支队的待办；待办口径在双周报页与 Hero，不在这一列。 */
          `<span class="nb">${IC.info}${firstPeriod ? '首份双周报提交后展示' : '暂无可公开摘要'}</span>`
    }</td>
    <td class="gapcell">${
      /* 🔴 `r.last` 现在是**手填的活动日期**（`YYYY-MM-DD`，2026-09-09 由录入时间改过来）——
           **没有时分了，不要再 slice(11,16)**，那样只会切出空串。
         ⚠ 第二行是「N 天前 · 共 N 次」：`gapDays` 按活动日期算，`actTotal` 是**累计**条数
           （不限时间窗，与 Hero 的「最近 14 天活动」不是一个口径，数字不同是对的）。
         ⚠ 没有记录时**只写「暂无打卡记录」，不写「共 0 次」**——一句话说清就够了。
         🔴 2026-09-18：`gapDays === 0` 写「今天」，**不写「0 天前」**（用户反馈：读起来像没打卡）。
           ⚠ 这里只换措辞，**不重算任何数**——`gapDays` 仍由服务端给，红线 4 不破。 */
      r.last
        ? `<b class="num">${esc(r.last.slice(5, 10))}</b><s class="num">${
            r.gapDays === 0 ? '今天' : `${r.gapDays} 天前`
          } · 共 ${r.actTotal} 次</s>`
        : `<b class="dash">—</b><s>暂无打卡记录</s>`
    }</td>
    <td class="chev">${IC.chev}</td>
  </tr>`;
}

/** 表头。⚠ 只剩四个可排序列——三列已删，它们的排序键在服务端也没有了 */
function thHtml(key: OverviewSortKey, label: string, v: OverviewView, extra = ''): string {
  const on = v.sort === key;
  const dir = on ? (v.dir === 'asc' ? '升序' : '降序') : '';
  return `
  <th class="${on ? 'on ' + v.dir : ''}" ${on ? `aria-sort="${v.dir === 'asc' ? 'ascending' : 'descending'}"` : ''} ${extra}>
    <button class="sortbtn" data-sort="${key}" title="${esc(label)}${on ? '：当前' + dir + '，点击切换' : '：点击排序'}">${esc(label)}${IC.up}</button>
  </th>`;
}

export function matrixHtml(v: OverviewView): string {
  if (!v.matrix.length) {
    return `<section class="panel" aria-label="队伍运营矩阵">
      <div class="panel-top"><h2>队伍运营矩阵</h2></div>
      ${emptyHtml(
        EIC.team,
        '当前没有生效的队伍',
        '已归档队伍已退出考核，其档案仍可在队伍管理中查看',
        '<button class="tbtn" data-nav="#/teams">去队伍管理</button>',
      )}
    </section>`;
  }
  /* ⚠ `panel-top` 里【只有标题】：着色切换器与四个筛选 chip 整行已于 2026-08-20 删除。
     ⚠ 表格加 `ovm` 类：三列删掉之后列宽要重配，而 `06_overview.css` 与冻结的一期逐字节一致，
       所以新列宽写在**新增的第 18 段**里（styles/README.md 纪律 1）。 */
  return `
  <section class="panel" aria-label="队伍运营矩阵">
    <div class="panel-top"><h2>队伍运营矩阵</h2>
      <span class="sub">共 <span class="num">${v.matrix.length}</span> 支队伍 · 点击任意一行可查看队伍详情</span>
    </div>
    <table class="matrix ovm">
      <colgroup>
        <col class="cg-team"><col class="cg-topic"><col class="cg-okr">
        <col class="cg-status"><col class="cg-brief"><col class="cg-gap"><col class="cg-chev">
      </colgroup>
      <thead>
        <tr>
          ${thHtml('id', '队伍', v)}
          <th>课题名称</th>
          ${thHtml('okr', 'OKR 进度', v)}
          ${thHtml('status', '最新提交', v, 'data-col="status"')}
          <th class="th-brief">最新进展摘要</th>
          ${thHtml('gap', '最近打卡', v)}
          <th></th>
        </tr>
      </thead>
      <tbody>${v.matrix.map((r) => rowHtml(r, v)).join('')}</tbody>
    </table>
  </section>`;
}

/**
 * 「最近活动」。
 * ⚠ 口径是**服务端 `feedOf()` 取的最近 9 场以内**——既不是本期活动、也不是近 14 天的全部，
 *   标题里不写「近 14 天 N 条」。
 * ⚠ 2026-08-24 副标题改为「共 M 场活动」，M 取服务端的 `feedTotal`（在用队伍的活动总数，不截断），
 *   **不是 `feed.length`**——后者封顶在 9，写成「共 9 场」会把"这一屏几张卡"说成"全营一共几场"。
 *   总数超过这一屏时才补「· 显示最近 N 场」；不超过就只写「共 M 场活动」（用户 2026-08-24 明确）。
 * 🔴 2026-10-07 「共 M 场活动」改为「累计 M 场」（用户要求）：Hero 改成近 14 天之后，
 *   「共」容易被读成同一个时间窗；「累计」把「不限时间」说清楚。口径未变，仍是 `feedTotal`。
 * ⚠ 卡片**直接复用日常打卡页的组件**（基线 §5.1：同一种卡片不维护两套样式），
 *   只关掉编辑 / 删除按钮。
 * ⚠ 2026-10-07 起**去掉外层白框**、卡片直接落在页面底色上，间距加大，照片默认压一档
 *   （用户反馈「很密集、看着不舒服」）。样式在 `22_overview_hero.css` ⑧，只限 `.ov-feed`，
 *   日常打卡页一个像素不变。
 */
export function feedHtml(d: OverviewData): string {
  const acts = d.view.feed;
  return `
  <section class="ov-feed" aria-label="最近活动">
    <div class="panel-top">
      <h2>最近活动</h2>
      <span class="sub">累计 ${d.view.feedTotal} 场${
        d.view.feedTotal > acts.length ? ` · 显示最近 ${acts.length} 场` : ''
      }</span>
    </div>
    <div class="ck-grid">${
      acts.length
        // 首屏一行三张走 eager，其余 lazy（见 checkins.ts 的 cardHtml）
        ? acts.map((a, i) => cardHtml(a, { ops: false, eager: i < 3 })).join('')
        : emptyHtml(
            EIC.cam,
            '还没有任何打卡记录',
            '各队录入打卡后，最近的活动会自动汇总到这里',
            `<button class="btn primary" data-ov="checkin">${IC.plus}打卡</button>`,
            'sm',
          )
    }</div>
  </section>`;
}

export const overviewHtml = (d: OverviewData): string =>
  `<div class="page">${heroHtml(d.view)}${matrixHtml(d.view)}${feedHtml(d)}</div>`;

/* ===================== 交互 ===================== */

/**
 * 参与人姓名表：只有点开活动详情时才需要。
 * ⚠ 首页是每个人每次 boot 都会打开的一页，**不在 load() 里顺手拉全营人员**——
 *   那是一份点开卡片才用得到的数据。这里按需拉一次并缓存。
 */
let namesCache: Record<string, string> | null = null;
async function ensureNames(): Promise<Record<string, string>> {
  if (namesCache) return namesCache;
  const people = await personApi.listPersons({});
  namesCache = Object.fromEntries(people.items.map((p) => [p.id, p.name]));
  return namesCache;
}

function openFeedCard(d: OverviewData, id: string): void {
  const a = d.view.feed.find((x) => x.id === id);
  if (!a) return;
  void (async () => {
    // 拉不到人名也要能看详情：弹窗里那几个名字会显示「（已删除人员）」，比整个点不开强
    const names = await ensureNames().catch(() => ({}) as Record<string, string>);
    viewModal(a as Activity, { names });
  })();
}

/** 表头排序：同一列再点一次换方向，换列时给一个合理的默认方向（一期同款） */
function toggleSort(key: OverviewSortKey): void {
  if (state.ov.sort === key) state.ov.dir = state.ov.dir === 'asc' ? 'desc' : 'asc';
  else {
    state.ov.sort = key;
    // 编号与最近打卡默认升序，其余默认降序（进度 / 最新提交都是"高的在前"更有用）
    state.ov.dir = key === 'id' || key === 'gap' ? 'asc' : 'desc';
  }
  refreshCurrent();
}

/* ===================== 页面模块 ===================== */

export const overviewPage = definePage<OverviewData>({
  skeleton: 'matrix',

  async load({ signal }) {
    const view = await overviewApi.overview(
      state.ov.sort as OverviewSortKey,
      state.ov.dir,
      { signal },
    );
    /* ⚠ **封面不再在这里换地址**（2026-08-23 图片提速）：地址由 `cardHtml` 里的
       `imgUrl()` 就地拼，`load()` 少一个往返。取不到图由 `data-imgfb` 兜到占位格。 */
    return { view };
  },

  html: (d) => overviewHtml(d),

  mounted() {
    const main = $('#main');
    if (main && state.ov.scroll) main.scrollTop = state.ov.scroll;
    /* Hero 动效：`.mo-in` 只在首次进页时由 render.ts 挂上——排序 / 打卡后的重拉不重播点亮与跳数 */
    const hero = $('.hero');
    const first = !!main?.querySelector('.page.mo-in');
    mountHeroFx(hero, first);
    if (first) countUp(hero);
  },

  onEvent(e, d) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    /* 键盘可达：矩阵行与活动卡片都是「Enter / 空格 = 打开」，与全站同一套 */
    if (e instanceof KeyboardEvent && e.type === 'keydown' && (e.key === 'Enter' || e.key === ' ')) {
      const row = t.closest<HTMLElement>('tr[data-team]');
      if (row?.dataset.team) {
        e.preventDefault();
        return openTeam(row.dataset.team);
      }
      const card = t.closest<HTMLElement>('.ck-card');
      if (card?.dataset.act) {
        e.preventDefault();
        return openFeedCard(d, card.dataset.act);
      }
      return;
    }

    if (e.type !== 'click') return;
    state.ov.scroll = $('#main')?.scrollTop ?? state.ov.scroll;

    const sort = t.closest<HTMLElement>('[data-sort]')?.dataset.sort;
    if (sort) return toggleSort(sort as OverviewSortKey);

    /* 一键打卡：直接开日常打卡的新增弹窗（弹窗本体只有一份，保存后 refreshCurrent 会刷新本页） */
    if (t.closest('[data-ov="checkin"]')) return openNewCheckin();

    /* ⚠ 活动卡片带的是 `data-ck="view|id"`（与打卡页同一套标记）：
       这里只认「打开详情」，编辑 / 删除按钮在首页根本没有渲染出来。 */
    const card = t.closest<HTMLElement>('.ck-card');
    if (card?.dataset.act) return openFeedCard(d, card.dataset.act);

    /* ⚠ 整行进队伍详情；但「最新提交」那一格是 `data-nav`，会被全局委托先接走
       （events.ts 里 data-nav 排在页面处理之前），所以点它跳双周报、点别处进详情。 */
    const row = t.closest<HTMLElement>('tr[data-team]');
    if (row?.dataset.team) return openTeam(row.dataset.team);
  },
});

/** 从首页进队伍详情：`from=overview` 决定详情页的面包屑与返回目标 */
function openTeam(id: string): void {
  go('#/overview/' + id);
}
